import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Switch } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import ListFilter from '@ts-private/list-filter';
import { ListFilterToolbar } from 'components/common/CommonStyles';
import { getDateFormat, getListFilterLocalizedStrings } from 'utils/functions';
import { getTranslationByCount } from 'constants/common';
import * as dictionariesActions from 'actions/actionCreators/dictionariesActions';
import documentTypeIds from 'constants/documentTypeIds';
import debouncePromise from 'debounce-promise';
import { debounce } from 'lodash';

const DEBOUNCE_DELAY = 500;

export const OfferListTopToolbar = props => {
  const {
    filters,
    userIsJustSalesUser,
    isSearchingRestrictedUserPartner,
    isSearchingRestrictedResponsible,
    visibleFilters,
    visibleFields,
    downloadEnabled,
    userPreferences,
    selectedUserPreference
  } = props;

  const {
    onUserPreferenceChanged,
    onAddUserPreference,
    onDeleteUserPreference,
    onListDeleteChanged,
    onListImplicitChanged,
    onResetFilters,
    onFilterValueChanged,
    onFilterVisibleValueChanged,
    onFieldsVisibleValueChanged,
    onDownload
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const getLimitTagsText = useCallback((more, singularTag, pluralTag) => getTranslationByCount(more, singularTag, pluralTag, t), [t]);

  const handleVisibleFilterChange = name => event => onFilterVisibleValueChanged(name, event.target.checked);
  const handleVisibleFieldsChange = name => event => onFieldsVisibleValueChanged(name, event.target.checked);

  const filtersToShow = visibleFilters.filter(x => x.isVisible).map(x => x.fieldName);

  const sortableColumns = [
    {
      columnName: 'InternalOfferDate',
      displayName: t('OfferList.OfferDate')
    },
    {
      columnName: 'OfferNumberSortable',
      displayName: t('OfferList.OfferNumber')
    },
    {
      columnName: 'OfferPriorityId',
      displayName: t('OfferList.Priority')
    },
    {
      columnName: 'FinancedAmount',
      displayName: t('OfferList.FinancedAmount')
    }
  ];

  const getSortableColumns = () => {
    return selectedUserPreference.order?.orderByColumns?.length > 0
      ? selectedUserPreference.order.orderByColumns.filter(x => {
          return visibleFields.find(y => x.columnName.toLowerCase().includes(y.fieldName.toLowerCase()) && y.isVisible === true);
        })
      : sortableColumns.filter(x => {
          return visibleFields.find(y => x.columnName.toLowerCase().includes(y.fieldName.toLowerCase()) && y.isVisible === true);
        });
  };

  const loadStates = () => dispatch(dictionariesActions.loadStates(documentTypeIds.Offer));
  const loadCurrencies = () => dispatch(dictionariesActions.loadCurrencies());
  const loadFinancingTypes = () => dispatch(dictionariesActions.loadFinancingTypes());
  const loadChannelTypes = () => dispatch(dictionariesActions.loadChannels());
  const loadApprovalTypes = () => dispatch(dictionariesActions.loadApprovalTypes());
  const loadPartnerPriorities = () => dispatch(dictionariesActions.loadPartnerPriorities());
  const loadTransactionCategories = () => dispatch(dictionariesActions.loadTransactionCategories());
  const loadSources = () => dispatch(dictionariesActions.loadSources());
  const loadVisibilityLevels = () => dispatch(dictionariesActions.loadOfferVisibilityLevels());
  const loadDealers = input => (input ? dispatch(dictionariesActions.loadDealers(input)) : Promise.resolve([]));
  const loadUsers = input =>
    filters.userPartner
      ? dispatch(dictionariesActions.loadUsers(input, filters.userPartner.partnerId))
      : Promise.resolve([]);
  const loadAccountManagers = input => (input ? dispatch(dictionariesActions.loadAccountManagers(input)) : Promise.resolve([]));
  const loadResponsibles = input => (input ? dispatch(dictionariesActions.loadResponsibles(input)) : Promise.resolve([]));
  const loadUserPartners = () =>
    dispatch(dictionariesActions.loadUserAssociatedPartners()).then(partners => {
      return partners.map(p => {
        return { ...p, userPartnerId: p.partnerId };
      });
    });

  const loadOfferTypes = debouncePromise(() => {
    return dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.Offer))
      .then(offerTypes => {
        const modificationOfferTypes = dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.OfferModification));
        return Promise.all([modificationOfferTypes, offerTypes]);
      })
      .then(([modificationOfferTypes, offerTypes]) => {
        const facilityOfferTypes = dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.FacilityFrameOffer));
        return Promise.all([facilityOfferTypes, modificationOfferTypes, offerTypes]);
      })
      .then(([facilityOfferTypes, modificationOfferTypes, offerTypes]) => {
        return offerTypes.concat(modificationOfferTypes).concat(facilityOfferTypes);
      });
  }, DEBOUNCE_DELAY);

  return (
    <ListFilterToolbar>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ListFilter
            sortableColumns={getSortableColumns()}
            filters={filters}
            onResetFilters={onResetFilters}
            visibleFilters={visibleFilters}
            visibleFields={visibleFields}
            onChangeFilterValue={onFilterValueChanged}
            handleVisibleFilterChange={handleVisibleFilterChange}
            handleVisibleFieldsChange={handleVisibleFieldsChange}
            onDownload={onDownload}
            downloadEnabled={downloadEnabled}
            downloadButtonVisible={true}
            entityName={'Offer'}
            visibleUserPreferences={true}
            userPreferences={userPreferences}
            searchPlaceholder={t('Placeholders.OfferListSearch')}
            onUserPreferenceChanged={onUserPreferenceChanged}
            selectedUserPreference={selectedUserPreference}
            onAddUserPreference={onAddUserPreference}
            onListDeleteChanged={onListDeleteChanged}
            onListImplicitChanged={onListImplicitChanged}
            onDeleteUserPreference={onDeleteUserPreference}
            searchTextMaxLength={255}
            localizedStrings={getListFilterLocalizedStrings(t)}
          >
            <Grid container spacing={2}>
              {filtersToShow.includes('responsibleId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_responsible'}
                    fullWidth
                    value={filters.responsible}
                    onChange={value => onFilterValueChanged('responsible', value)}
                    disabled={isSearchingRestrictedResponsible}
                    label={t('OfferList.Responsible')}
                    valueKey='responsibleId'
                    loadOptions={debounce(loadResponsibles, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('userPartnerId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_userPartner'}
                    fullWidth
                    value={filters.userPartner}
                    disabled={isSearchingRestrictedUserPartner}
                    onChange={value => onFilterValueChanged('userPartner', value)}
                    label={t('OfferList.UserPartner')}
                    valueKey='partnerId'
                    loadOptions={debounce(loadUserPartners, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('userId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_user'}
                    fullWidth
                    value={filters.user}
                    disabled={!filters.userPartner}
                    onChange={value => onFilterValueChanged('user', value)}
                    label={t('OfferList.User')}
                    valueKey='userId'
                    labelKey='userName'
                    loadOptions={debounce(loadUsers, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('dealerId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_dealer'}
                    fullWidth
                    value={filters.dealer}
                    onChange={value => onFilterValueChanged('dealer', value)}
                    label={t('OfferList.Dealer')}
                    valueKey='dealerId'
                    loadOptions={debounce(loadDealers, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('accountManagerId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_accountManager'}
                    fullWidth
                    value={filters.accountManager}
                    disabled={userIsJustSalesUser}
                    onChange={value => onFilterValueChanged('accountManager', value)}
                    label={t('OfferList.AccountManager')}
                    valueKey='accountManagerId'
                    loadOptions={debounce(loadAccountManagers, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('currencyId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_currency'}
                    fullWidth
                    value={filters.currency}
                    onChange={value => onFilterValueChanged('currency', value)}
                    label={t('OfferList.Currency')}
                    valueKey='currencyId'
                    labelKey='code'
                    loadOptions={debounce(loadCurrencies, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('offerTypeId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_offerType'}
                    fullWidth
                    value={filters.offerType}
                    onChange={value => onFilterValueChanged('offerType', value)}
                    label={t('OfferList.OfferType')}
                    valueKey='offerTypeId'
                    labelKey='offerTypeName'
                    loadOptions={debounce(loadOfferTypes, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('financingTypeId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_financingType'}
                    fullWidth
                    value={filters.financingType}
                    onChange={value => onFilterValueChanged('financingType', value)}
                    label={t('OfferList.FinancingType')}
                    valueKey='financingTypeId'
                    labelKey='financingTypeName'
                    loadOptions={debounce(loadFinancingTypes, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('channelTypeId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_channelType'}
                    fullWidth
                    value={filters.channelType}
                    onChange={value => onFilterValueChanged('channelType', value)}
                    label={t('OfferList.SalesChannel')}
                    valueKey='channelTypeId'
                    labelKey='channelTypeName'
                    loadOptions={debounce(loadChannelTypes, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('approvalTypeLevelId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_approvalType'}
                    fullWidth
                    value={filters.approvalTypeLevel}
                    onChange={value => onFilterValueChanged('approvalTypeLevel', value)}
                    label={t('OfferList.ApprovalType')}
                    valueKey='approvalTypeLevelId'
                    labelKey='levelName'
                    loadOptions={debounce(loadApprovalTypes, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('offerPriorityId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_partnerPriority'}
                    fullWidth
                    value={filters.offerPriority}
                    onChange={value => onFilterValueChanged('offerPriority', value)}
                    label={t('OfferList.OfferPriority')}
                    valueKey='offerPriorityId'
                    labelKey='offerPriorityName'
                    loadOptions={debounce(loadPartnerPriorities, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('transactionCategoryId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_transactionCategory'}
                    fullWidth
                    value={filters.transactionCategory}
                    onChange={value => onFilterValueChanged('transactionCategory', value)}
                    label={t('OfferList.TransactionCategory')}
                    valueKey='transactionCategoryId'
                    labelKey='transactionCategoryName'
                    loadOptions={debounce(loadTransactionCategories, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('offerNumber') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <TextField
                    id={'offerList_filters_offerNumber'}
                    fullWidth
                    label={t('OfferList.OfferNumber')}
                    value={filters.offerNumber || ''}
                    onChange={value => onFilterValueChanged('offerNumber', value)}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>
              )}
              {filtersToShow.includes('clientName') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <TextField
                    id={'offerList_filters_clientName'}
                    fullWidth
                    label={t('OfferList.ClientName')}
                    value={filters.clientName || ''}
                    onChange={value => onFilterValueChanged('clientName', value)}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>
              )}
              {filtersToShow.includes('stateIds') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_state'}
                    fullWidth
                    isMultiSelection
                    withCheckboxes
                    limitTags={0}
                    renderTags={selected => {
                      if (selected.length === 0) return null;
                      const label = selected.length === 1 ? selected[0].name : getLimitTagsText(selected.length, 'Selection', 'Selections');
                      return <span style={theme.typography.defaultFont}>{label}</span>;
                    }}
                    value={filters.states || []}
                    onChange={value => onFilterValueChanged('states', value)}
                    label={t('OfferList.State')}
                    valueKey='stateId'
                    loadOptions={debounce(loadStates, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('isActive') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        id={'offerList_filters_isActive'}
                        checked={JSON.parse(filters.isActive)}
                        onChange={e => onFilterValueChanged('isActive', e.target.checked)}
                        value='1'
                      />
                    }
                    label={t('OfferList.IsActive')}
                  />
                </Grid>
              )}
              {filtersToShow.includes('visibilityLevelId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_visibilityLevel'}
                    fullWidth
                    value={filters.visibilityLevel}
                    onChange={value => onFilterValueChanged('visibilityLevel', value)}
                    label={t('OfferList.VisibilityLevel')}
                    valueKey='visibilityLevelId'
                    loadOptions={debounce(loadVisibilityLevels, DEBOUNCE_DELAY, { leading: true })}
                  />
                </Grid>
              )}
              {filtersToShow.includes('sourceTypeId') && (
                <Grid item xs={12} sm={6} lg={4}>
                  <Autocomplete
                    id={'offerList_filters_sourceType'}
                    fullWidth
                    value={filters.sourceType}
                    onChange={value => onFilterValueChanged('sourceType', value)}
                    label={t('OfferList.Source')}
                    valueKey='sourceTypeId'
                    labelKey='sourceTypeName'
                    loadOptions={debounce(loadSources, DEBOUNCE_DELAY, { leading: true })}
                    isClearable
                  />
                </Grid>
              )}
              {filtersToShow.includes('offerStartDate') && (
                <Grid item xs={12} sm={6} lg={4} id={'offerList_filters_offerStartDate'}>
                  <DateTime
                    value={filters.offerStartDate}
                    label={t('OfferList.OfferStartDate')}
                    onChange={value => onFilterValueChanged('offerStartDate', value)}
                    isClearable={true}
                    format={getDateFormat()}
                  />
                </Grid>
              )}
              {filtersToShow.includes('offerEndDate') && (
                <Grid item xs={12} sm={6} lg={4} id={'offerList_filters_offerEndDate'}>
                  <DateTime
                    value={filters.offerEndDate}
                    label={t('OfferList.OfferEndDate')}
                    onChange={value => onFilterValueChanged('offerEndDate', value)}
                    isClearable={true}
                    format={getDateFormat()}
                  />
                </Grid>
              )}
            </Grid>
          </ListFilter>
        </Grid>
      </Grid>
    </ListFilterToolbar>
  );
};

OfferListTopToolbar.propTypes = {
  filters: PropTypes.shape({
    fullTextFilter: PropTypes.string.isRequired,
    offerNumber: PropTypes.string,
    offerStartDate: PropTypes.string,
    offerEndDate: PropTypes.string,
    clientName: PropTypes.string,
    dealer: PropTypes.object,
    user: PropTypes.object,
    userPartner: PropTypes.object,
    accountManager: PropTypes.object,
    responsible: PropTypes.object,
    states: PropTypes.array,
    channelType: PropTypes.object,
    approvalTypeLevel: PropTypes.object,
    currency: PropTypes.object,
    financingType: PropTypes.object,
    offerPriority: PropTypes.object,
    transactionCategory: PropTypes.object,
    sourceType: PropTypes.object,
    offerType: PropTypes.object,
    visibilityLevel: PropTypes.object,
    isActive: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  }),
  onFilterValueChanged: PropTypes.func.isRequired,
  onFilterVisibleValueChanged: PropTypes.func.isRequired,
  onFieldsVisibleValueChanged: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  downloadEnabled: PropTypes.bool.isRequired,
  isSearchingRestrictedUserPartner: PropTypes.bool,
  isSearchingRestrictedResponsible: PropTypes.bool,
  visibleFilters: PropTypes.array.isRequired,
  visibleFields: PropTypes.array.isRequired,
  userIsJustSalesUser: PropTypes.bool,
  userPreferences: PropTypes.array,
  onUserPreferenceChanged: PropTypes.func,
  selectedUserPreference: PropTypes.object,
  onAddUserPreference: PropTypes.func,
  onDeleteUserPreference: PropTypes.func,
  onListDeleteChanged: PropTypes.func,
  onListImplicitChanged: PropTypes.func
};

export default OfferListTopToolbar;
